import React, { useEffect, useMemo, useState } from "react";
import { Users, Building2, Download, Trash2, Search, RefreshCw, MapPin, MessageSquare, Smartphone, Gift, Plus, CreditCard, CheckCircle2, AlertTriangle, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

const STATUSES = ["new", "contacted", "converted", "archived"];
const STATUS_STYLES = {
  new: "bg-[#F6DED5] text-[#B54828]",
  contacted: "bg-[#DCE7DD] text-[#456B49]",
  converted: "bg-[#D6EAD8] text-[#2F6B36]",
  archived: "bg-[#ECE8E1] text-[#8A837A]",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso || "";
  }
}

function toCsv(rows, cols) {
  const head = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          const v = r[c.key] ?? "";
          const s = String(v).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        })
        .join(",")
    )
    .join("\n");
  return `${head}\n${body}`;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StatusSelect({ value, onChange, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`text-xs font-bold rounded-full px-3 py-1.5 border-0 cursor-pointer focus:ring-2 focus:ring-[#E86A45] outline-none ${STATUS_STYLES[value] || STATUS_STYLES.new}`}
    >
      {STATUSES.map((s) => (
        <option key={s} value={s}>
          {s.charAt(0).toUpperCase() + s.slice(1)}
        </option>
      ))}
    </select>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E3DA] px-5 py-4">
      <p className="text-3xl font-extrabold text-[#2A2724]">{value}</p>
      <p className="text-sm font-bold text-[#4A4746]">{label}</p>
      {sub && <p className="text-xs text-[#9A938B] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function LeadsDashboard() {
  const [tab, setTab] = useState("waitlist");
  const [loading, setLoading] = useState(true);
  const [waitlist, setWaitlist] = useState([]);
  const [byCity, setByCity] = useState({});
  const [vetLeads, setVetLeads] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [optins, setOptins] = useState([]);
  const [clinicGroups, setClinicGroups] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [digestStatus, setDigestStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [waitlistApp, setWaitlistApp] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const capture = (p, label) =>
        p.catch((e) => {
          const status = e?.status;
          setLoadError((prev) =>
            prev?.type === "auth"
              ? prev
              : (status === 401 || status === 403)
              ? { type: "auth", status, label }
              : { type: "error", status: status || "network", label, message: e?.message }
          );
          return { __error: true, status, label };
        });
      const [wl, vl, sub, opt, cc, ss] = await Promise.all([
        capture(apiFetch("/admin/waitlist"), "waitlist"),
        capture(apiFetch("/admin/vet-leads"), "vet-leads"),
        capture(apiFetch("/growth/admin/submissions"), "submissions"),
        capture(apiFetch("/growth/admin/sms-optins"), "sms-optins"),
        capture(apiFetch("/admin/clinic-comp"), "clinic-comp"),
        apiFetch("/billing/admin/stripe-status").catch(() => null),
      ]);
      setWaitlist(wl.items || []);
      setByCity(wl.by_city || {});
      setVetLeads(vl.items || []);
      setSubmissions(sub.items || []);
      setOptins(opt.items || []);
      setClinicGroups(cc.groups || []);
      setStripeStatus(ss);
      apiFetch("/admin/leads-digest/status").then(setDigestStatus).catch(() => setDigestStatus(null));
    } catch (e) {
      toast.error(e.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setWaitlistStatus = async (id, status) => {
    setWaitlist((prev) => prev.map((w) => (w.id === id ? { ...w, status } : w)));
    try {
      await apiFetch(`/admin/waitlist/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      toast.success("Status updated");
    } catch (e) {
      toast.error(e.message || "Update failed");
      load();
    }
  };

  const setVetStatus = async (id, status) => {
    setVetLeads((prev) => prev.map((v) => (v.id === id ? { ...v, status } : v)));
    try {
      await apiFetch(`/admin/vet-leads/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      toast.success("Status updated");
    } catch (e) {
      toast.error(e.message || "Update failed");
      load();
    }
  };

  const removeWaitlist = async (id) => {
    if (!window.confirm("Delete this waitlist signup?")) return;
    try {
      await apiFetch(`/admin/waitlist/${id}`, { method: "DELETE" });
      setWaitlist((prev) => prev.filter((w) => w.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  const removeVet = async (id) => {
    if (!window.confirm("Delete this vet lead?")) return;
    try {
      await apiFetch(`/admin/vet-leads/${id}`, { method: "DELETE" });
      setVetLeads((prev) => prev.filter((v) => v.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  const sendDigestNow = async () => {
    try {
      const res = await apiFetch("/admin/leads-digest/run", { method: "POST" });
      if (res.ok) toast.success(`Digest sent to ${res.sent_to}`);
      else toast.error("Digest send failed — check email settings");
      apiFetch("/admin/leads-digest/status").then(setDigestStatus).catch(() => {});
    } catch (e) {
      toast.error(e.message || "Failed to send digest");
    }
  };

  const removeSubmission = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await apiFetch(`/growth/admin/submissions/${id}`, { method: "DELETE" });
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  const filteredWaitlist = useMemo(() => {
    let list = waitlist;
    if (waitlistApp !== "all") list = list.filter((w) => (w.app || "wooflect") === waitlistApp);
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (w) =>
        (w.email || "").toLowerCase().includes(s) ||
        (w.name || "").toLowerCase().includes(s) ||
        (w.city || "").toLowerCase().includes(s) ||
        (w.app || "").toLowerCase().includes(s) ||
        (w.role || "").toLowerCase().includes(s) ||
        (w.source || "").toLowerCase().includes(s)
    );
  }, [waitlist, q, waitlistApp]);

  const filteredVets = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return vetLeads;
    return vetLeads.filter(
      (v) =>
        (v.clinic_name || "").toLowerCase().includes(s) ||
        (v.contact_name || "").toLowerCase().includes(s) ||
        (v.email || "").toLowerCase().includes(s) ||
        (v.city || "").toLowerCase().includes(s)
    );
  }, [vetLeads, q]);

  const filteredSubs = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return submissions;
    return submissions.filter(
      (x) =>
        (x.name || "").toLowerCase().includes(s) ||
        (x.user_email || "").toLowerCase().includes(s) ||
        (x.message || "").toLowerCase().includes(s)
    );
  }, [submissions, q]);

  const filteredOptins = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return optins;
    return optins.filter(
      (x) =>
        (x.phone || "").toLowerCase().includes(s) ||
        (x.name || "").toLowerCase().includes(s) ||
        (x.consent_text || "").toLowerCase().includes(s)
    );
  }, [optins, q]);

  const exportCsv = () => {
    if (tab === "waitlist") {
      download(
        "wooflect-waitlist.csv",
        toCsv(filteredWaitlist, [
          { key: "email", label: "Email" },
          { key: "name", label: "Name" },
          { key: "app", label: "App" },
          { key: "role", label: "Role" },
          { key: "city", label: "City" },
          { key: "source", label: "Source" },
          { key: "status", label: "Status" },
          { key: "created_at", label: "Joined" },
        ])
      );
    } else if (tab === "vet-leads") {
      download(
        "wooflect-vet-leads.csv",
        toCsv(filteredVets, [
          { key: "clinic_name", label: "Clinic" },
          { key: "contact_name", label: "Contact" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "city", label: "City" },
          { key: "plan_interest", label: "Plan" },
          { key: "status", label: "Status" },
          { key: "message", label: "Message" },
          { key: "created_at", label: "Received" },
        ])
      );
    } else if (tab === "submissions") {
      download(
        "wooflect-submissions.csv",
        toCsv(filteredSubs, [
          { key: "name", label: "Name" },
          { key: "user_email", label: "Email" },
          { key: "message", label: "Message" },
          { key: "created_at", label: "Received" },
        ])
      );
    } else {
      download(
        "wooflect-sms-optins.csv",
        toCsv(filteredOptins, [
          { key: "phone", label: "Phone" },
          { key: "name", label: "Name" },
          { key: "consent", label: "Consent" },
          { key: "consent_text", label: "Consent Text" },
          { key: "source", label: "Source" },
          { key: "ip", label: "IP" },
          { key: "created_at", label: "Date" },
        ])
      );
    }
    toast.success("CSV exported");
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="leads-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#2A2724]">Leads Dashboard</h1>
          <p className="text-[#6B6560] font-semibold mt-1">Manage beta waitlist signups and vet-clinic leads.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="leads-refresh-btn"
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-[#4A4746] bg-white border border-[#E8E3DA] hover:bg-[#F2EFE9] transition-colors"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            data-testid="leads-export-btn"
            onClick={exportCsv}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white bg-[#6F8F72] hover:bg-[#61805f] transition-colors"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Waitlist signups" value={waitlist.length} />
        <StatCard label="Vet leads" value={vetLeads.length} />
        <StatCard
          label="Top city"
          value={Object.entries(byCity).sort((a, b) => b[1] - a[1])[0]?.[0]?.split("-")[0] || "—"}
          sub={Object.entries(byCity).sort((a, b) => b[1] - a[1])[0]?.[1] ? `${Object.entries(byCity).sort((a, b) => b[1] - a[1])[0][1]} signups` : ""}
        />
        <StatCard
          label="New / unhandled"
          value={
            [...waitlist, ...vetLeads].filter((x) => (x.status || "new") === "new").length
          }
        />
      </div>

      {/* Permission / load error */}
      {loadError?.type === "auth" && (
        <div data-testid="admin-perm-error" className="flex items-start gap-3 rounded-2xl border border-[#E6B4A6] bg-[#FBE4DC] text-[#8A3A22] px-4 py-3 mb-6">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm font-semibold leading-snug">
            This account does not have admin access (server returned {loadError.status} on “{loadError.label}”).
            <div className="font-medium opacity-90 mt-0.5">
              The waitlist is hidden because the logged-in email is not on the admin allowlist for this environment.
              Log in with an admin account, or add this email to <code>ADMIN_EMAILS</code> in the backend env and redeploy.
            </div>
          </div>
        </div>
      )}

      {loadError?.type === "error" && (
        <div data-testid="admin-load-error" className="flex items-start gap-3 rounded-2xl border border-[#EBC79A] bg-[#FBE9D3] text-[#8A5A1C] px-4 py-3 mb-6">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm font-semibold leading-snug">
            Couldn&rsquo;t load “{loadError.label}” (error {String(loadError.status)}).
            <div className="font-medium opacity-90 mt-0.5">
              The list may be incomplete. {loadError.message ? `Details: ${loadError.message}. ` : ""}
              Try Refresh — if it persists, the server hit an error on this section.
            </div>
          </div>
        </div>
      )}

      {/* Stripe status */}
      {stripeStatus && (() => {
        const live = stripeStatus.is_live;
        const charges = stripeStatus.charges_enabled;
        const ready = live && charges;
        const tone = ready
          ? "bg-[#D6EAD8] border-[#A9CDAE] text-[#2F6B36]"
          : live
          ? "bg-[#FBE9D3] border-[#EBC79A] text-[#8A5A1C]"
          : "bg-[#E7EDF6] border-[#BFD0E8] text-[#3A5579]";
        return (
          <div data-testid="stripe-status-banner" className={`flex items-start gap-3 rounded-2xl border px-4 py-3 mb-6 ${tone}`}>
            <CreditCard size={18} className="mt-0.5 shrink-0" />
            <div className="text-sm font-semibold leading-snug">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  {ready ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                  Stripe mode:
                  <span data-testid="stripe-mode" className="uppercase font-extrabold tracking-wide">{stripeStatus.mode}</span>
                </span>
                {live ? (
                  <span data-testid="stripe-charges">· Charges {charges ? "enabled ✅" : "NOT enabled ⚠️"}</span>
                ) : (
                  <span>· Sandbox — no real charges (preview)</span>
                )}
              </div>
              <div className="font-medium opacity-90 mt-0.5">
                {live
                  ? ready
                    ? "You are LIVE. Real cards will be charged."
                    : "Live key detected but the account can't accept charges yet — finish Stripe account activation."
                  : "Test mode in this environment. Payments go live automatically after you deploy to production."}
                {stripeStatus.account_id ? <span className="opacity-70"> · acct {stripeStatus.account_id}</span> : null}
                {!live && stripeStatus.products_ready ? <span className="opacity-70"> · products ✓</span> : null}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Daily digest diagnostics */}
      {digestStatus && (() => {
        const alive = digestStatus.loop_alive;
        const sentToday = digestStatus.last_sent_date === (digestStatus.pacific_now || "").slice(0, 10);
        const tone = alive
          ? "bg-[#D6EAD8] border-[#A9CDAE] text-[#2F6B36]"
          : "bg-[#FBE9D3] border-[#EBC79A] text-[#8A5A1C]";
        return (
          <div data-testid="digest-status-banner" className={`flex items-start gap-3 rounded-2xl border px-4 py-3 mb-6 ${tone}`}>
            <Clock size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1 text-sm font-semibold leading-snug">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  {alive ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                  Daily 5 AM digest timer:
                  <span data-testid="digest-alive" className="font-extrabold">{alive ? "alive ✅" : "idle ⚠️"}</span>
                </span>
                <span className="opacity-80">· {digestStatus.pacific_now}</span>
              </div>
              <div className="font-medium opacity-90 mt-0.5">
                {sentToday
                  ? `Sent today at ${fmtDateTime(digestStatus.last_sent_at)} → ${digestStatus.recipient}`
                  : digestStatus.last_sent_at
                  ? `Last sent ${fmtDateTime(digestStatus.last_sent_at)} → ${digestStatus.recipient}. A backup trigger also fires on the first visit after 5 AM PT.`
                  : `Not sent yet. It sends at 5 AM Pacific, with a backup on the first visit after 5 AM → ${digestStatus.recipient}.`}
              </div>
            </div>
            <button
              data-testid="digest-send-now-btn"
              onClick={sendDigestNow}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold text-white bg-[#6F8F72] hover:bg-[#61805f] transition-colors shrink-0"
            >
              <Send size={14} /> Send now
            </button>
          </div>
        );
      })()}

      <div className="flex items-center gap-2 mb-4">
        <button
          data-testid="tab-waitlist"
          onClick={() => setTab("waitlist")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
            tab === "waitlist" ? "bg-[#E86A45] text-white" : "bg-white border border-[#E8E3DA] text-[#4A4746] hover:bg-[#F2EFE9]"
          }`}
        >
          <Users size={16} /> Waitlist ({waitlist.length})
        </button>
        <button
          data-testid="tab-vet-leads"
          onClick={() => setTab("vet-leads")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
            tab === "vet-leads" ? "bg-[#E86A45] text-white" : "bg-white border border-[#E8E3DA] text-[#4A4746] hover:bg-[#F2EFE9]"
          }`}
        >
          <Building2 size={16} /> Vet Leads ({vetLeads.length})
        </button>
        <button
          data-testid="tab-submissions"
          onClick={() => setTab("submissions")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
            tab === "submissions" ? "bg-[#E86A45] text-white" : "bg-white border border-[#E8E3DA] text-[#4A4746] hover:bg-[#F2EFE9]"
          }`}
        >
          <MessageSquare size={16} /> Submissions ({submissions.length})
        </button>
        <button
          data-testid="tab-sms-optins"
          onClick={() => setTab("sms-optins")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
            tab === "sms-optins" ? "bg-[#E86A45] text-white" : "bg-white border border-[#E8E3DA] text-[#4A4746] hover:bg-[#F2EFE9]"
          }`}
        >
          <Smartphone size={16} /> SMS Opt-Ins ({optins.length})
        </button>
        <button
          data-testid="tab-clinics"
          onClick={() => setTab("clinics")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
            tab === "clinics" ? "bg-[#E86A45] text-white" : "bg-white border border-[#E8E3DA] text-[#4A4746] hover:bg-[#F2EFE9]"
          }`}
        >
          <Gift size={16} /> Clinic Comps ({clinicGroups.length})
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A938B]" />
        <input
          data-testid="leads-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tab === "waitlist" ? "Search by email, city, source…" : tab === "sms-optins" ? "Search by phone, name, consent…" : "Search by clinic, contact, email, city…"}
          className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-[#E8E3DA] text-[#2A2724] font-semibold focus:ring-2 focus:ring-[#E86A45] outline-none"
        />
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-[#E8E3DA] p-10 text-center text-[#9A938B] font-semibold" data-testid="leads-loading">
          Loading leads…
        </div>
      ) : tab === "waitlist" ? (
        <>
          <div className="flex items-center gap-2 mb-3" data-testid="waitlist-app-filter">
            {["all", "wooflect", "purrflect"].map((a) => (
              <button
                key={a}
                data-testid={`waitlist-app-filter-${a}`}
                onClick={() => setWaitlistApp(a)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${
                  waitlistApp === a ? "bg-[#2A2724] text-white" : "bg-white border border-[#E8E3DA] text-[#4A4746] hover:bg-[#F2EFE9]"
                }`}
              >
                {a === "all" ? "All" : a} {a !== "all" ? `(${waitlist.filter((w) => (w.app || "wooflect") === a).length})` : `(${waitlist.length})`}
              </button>
            ))}
          </div>
          <WaitlistTable rows={filteredWaitlist} onStatus={setWaitlistStatus} onDelete={removeWaitlist} />
        </>
      ) : tab === "vet-leads" ? (
        <VetLeadsTable rows={filteredVets} onStatus={setVetStatus} onDelete={removeVet} />
      ) : tab === "submissions" ? (
        <SubmissionsTable rows={filteredSubs} onDelete={removeSubmission} />
      ) : tab === "clinics" ? (
        <ClinicCompPanel groups={clinicGroups} onChanged={load} />
      ) : (
        <OptInsTable rows={filteredOptins} />
      )}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E3DA] p-10 text-center" data-testid="leads-empty">
      <p className="text-[#4A4746] font-bold">No {label} yet</p>
      <p className="text-sm text-[#9A938B] mt-1">New entries will appear here as they come in.</p>
    </div>
  );
}

function WaitlistTable({ rows, onStatus, onDelete }) {
  if (!rows.length) return <EmptyState label="waitlist signups" />;
  return (
    <div className="bg-white rounded-2xl border border-[#E8E3DA] overflow-hidden" data-testid="waitlist-table">
      <div className="divide-y divide-[#F0ECE4]">
        {rows.map((w) => {
          const app = w.app || "wooflect";
          const isPurr = app === "purrflect";
          return (
          <div key={w.id} className="flex items-center gap-4 px-5 py-3.5 flex-wrap" data-testid={`waitlist-row-${w.id}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  data-testid={`waitlist-app-${w.id}`}
                  className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${isPurr ? "bg-[#EDE7F6] text-[#6E4CA0]" : "bg-[#FBE9D3] text-[#B8611C]"}`}
                >
                  {isPurr ? "Purrflect" : "Wooflect"}
                </span>
                {w.role === "vet_clinic" && (
                  <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#E7EDF6] text-[#3A5579]">Vet clinic</span>
                )}
                <p className="font-bold text-[#2A2724] break-all">{w.name ? `${w.name} · ` : ""}{w.email}</p>
              </div>
              <p className="text-xs text-[#9A938B] flex items-center gap-2 mt-0.5">
                {w.city ? <><MapPin size={12} /> {w.city}</> : null}
                {w.source && w.source !== "direct" ? <span className="text-[#B54828]">· {w.source}</span> : null}
                <span>· {fmtDate(w.created_at)}</span>
              </p>
            </div>
            <StatusSelect value={w.status || "new"} onChange={(s) => onStatus(w.id, s)} testid={`waitlist-status-${w.id}`} />
            <button
              data-testid={`waitlist-delete-${w.id}`}
              onClick={() => onDelete(w.id)}
              className="p-2 rounded-full text-[#B54828] hover:bg-[#F6DED5] transition-colors"
            >
              <Trash2 size={16} />
            </button>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function VetLeadsTable({ rows, onStatus, onDelete }) {
  if (!rows.length) return <EmptyState label="vet leads" />;
  return (
    <div className="bg-white rounded-2xl border border-[#E8E3DA] overflow-hidden" data-testid="vet-leads-table">
      <div className="divide-y divide-[#F0ECE4]">
        {rows.map((v) => (
          <div key={v.id} className="px-5 py-4" data-testid={`vet-lead-row-${v.id}`}>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <p className="font-bold text-[#2A2724]">{v.clinic_name}</p>
                <p className="text-sm text-[#4A4746]">
                  {v.contact_name} · <span className="break-all">{v.email}</span>
                  {v.phone ? ` · ${v.phone}` : ""}
                </p>
                <p className="text-xs text-[#9A938B] flex items-center gap-2 mt-0.5">
                  <MapPin size={12} /> {v.city} · Plan: {v.plan_interest} · {fmtDate(v.created_at)}
                </p>
              </div>
              <StatusSelect value={v.status || "new"} onChange={(s) => onStatus(v.id, s)} testid={`vet-status-${v.id}`} />
              <button
                data-testid={`vet-delete-${v.id}`}
                onClick={() => onDelete(v.id)}
                className="p-2 rounded-full text-[#B54828] hover:bg-[#F6DED5] transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
            {v.message ? (
              <p className="text-sm text-[#6B6560] mt-2 bg-[#FAF8F4] rounded-xl px-3 py-2 border border-[#F0ECE4]">
                “{v.message}”
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}


function SubmissionsTable({ rows, onDelete }) {
  if (!rows.length) return <EmptyState label="submissions" />;
  return (
    <div className="bg-white rounded-2xl border border-[#E8E3DA] overflow-hidden" data-testid="submissions-table">
      <div className="divide-y divide-[#F0ECE4]">
        {rows.map((s) => (
          <div key={s.id} className="px-5 py-4 flex items-start gap-4" data-testid={`submission-row-${s.id}`}>
            {s.photo_base64 && (
              <img
                src={s.photo_base64.startsWith("data:") ? s.photo_base64 : `data:image/jpeg;base64,${s.photo_base64}`}
                alt=""
                className="h-16 w-16 rounded-xl object-cover shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#2A2724]">{s.name || "Anonymous"}</p>
              <p className="text-xs text-[#9A938B] break-all">{s.user_email} · {fmtDate(s.created_at)}</p>
              {s.message ? <p className="text-sm text-[#4A4746] mt-1.5">{s.message}</p> : null}
              {s.photo_base64 && <p className="text-xs text-[#6F8F72] font-bold mt-1">📎 Photo attached</p>}
            </div>
            <button
              data-testid={`submission-delete-${s.id}`}
              onClick={() => onDelete(s.id)}
              className="p-2 rounded-full text-[#B54828] hover:bg-[#F6DED5] transition-colors shrink-0"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
}

function ClinicCompPanel({ groups, onChanged }) {
  const [mode, setMode] = useState("email");
  const [clinicName, setClinicName] = useState("");
  const [emails, setEmails] = useState("");
  const [domain, setDomain] = useState("");
  const [days, setDays] = useState(90);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!clinicName.trim()) return toast.error("Clinic name is required");
    setBusy(true);
    try {
      let body;
      if (mode === "email") {
        const list = emails.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
        if (!list.length) { setBusy(false); return toast.error("Add at least one valid email"); }
        body = { clinic_name: clinicName.trim(), type: "email", emails: list, note };
      } else {
        if (!domain.trim().includes(".")) { setBusy(false); return toast.error("Enter a valid domain, e.g. happypaws.com"); }
        body = { clinic_name: clinicName.trim(), type: "domain", domain: domain.trim().replace(/^@/, ""), enrollment_days: Number(days) || 90, note };
      }
      const res = await apiFetch("/admin/clinic-comp", { method: "POST", body: JSON.stringify(body) });
      toast.success(`Added. ${res.granted_now} existing staff granted lifetime Pro.`);
      setClinicName(""); setEmails(""); setDomain(""); setNote("");
      onChanged();
    } catch (e) {
      toast.error(e.message || "Failed to add clinic comp");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (rid) => {
    if (!window.confirm("Deactivate this rule? Staff already granted keep lifetime Pro.")) return;
    try {
      await apiFetch(`/admin/clinic-comp/${rid}`, { method: "DELETE" });
      toast.success("Rule deactivated");
      onChanged();
    } catch (e) {
      toast.error(e.message || "Failed");
    }
  };

  return (
    <div data-testid="clinic-comp-panel">
      {/* Add form */}
      <div className="bg-white rounded-2xl border border-[#E8E3DA] p-5 mb-5">
        <p className="font-extrabold text-[#2A2724] mb-3 flex items-center gap-2"><Gift size={18} color="#6F8F72" /> Grant clinic staff lifetime Pro</p>
        <div className="flex gap-2 mb-3">
          <button data-testid="clinic-mode-email" onClick={() => setMode("email")} className={`px-4 py-2 rounded-full text-sm font-bold ${mode === "email" ? "bg-[#6F8F72] text-white" : "bg-[#F2EFE9] text-[#4A4746]"}`}>By staff emails</button>
          <button data-testid="clinic-mode-domain" onClick={() => setMode("domain")} className={`px-4 py-2 rounded-full text-sm font-bold ${mode === "domain" ? "bg-[#6F8F72] text-white" : "bg-[#F2EFE9] text-[#4A4746]"}`}>By email domain</button>
        </div>
        <input data-testid="clinic-name-input" value={clinicName} onChange={(e) => setClinicName(e.target.value)} placeholder="Clinic name (e.g. Portland Paws Clinic)" className="w-full mb-2 px-4 py-3 rounded-xl bg-[#FAF8F4] border border-[#E8E3DA] font-semibold outline-none focus:ring-2 focus:ring-[#6F8F72]" />
        {mode === "email" ? (
          <textarea data-testid="clinic-emails-input" value={emails} onChange={(e) => setEmails(e.target.value)} rows={3} placeholder="Staff emails — paste any number, separated by commas, spaces or new lines" className="w-full mb-2 px-4 py-3 rounded-xl bg-[#FAF8F4] border border-[#E8E3DA] font-semibold outline-none focus:ring-2 focus:ring-[#6F8F72]" />
        ) : (
          <div className="flex gap-2 mb-2 flex-wrap">
            <input data-testid="clinic-domain-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="happypaws.com" className="flex-1 min-w-[180px] px-4 py-3 rounded-xl bg-[#FAF8F4] border border-[#E8E3DA] font-semibold outline-none focus:ring-2 focus:ring-[#6F8F72]" />
            <div className="flex items-center gap-2">
              <input data-testid="clinic-days-input" type="number" value={days} onChange={(e) => setDays(e.target.value)} className="w-24 px-3 py-3 rounded-xl bg-[#FAF8F4] border border-[#E8E3DA] font-semibold outline-none focus:ring-2 focus:ring-[#6F8F72]" />
              <span className="text-sm text-[#6B6560] font-semibold">day window</span>
            </div>
          </div>
        )}
        <input data-testid="clinic-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="w-full mb-3 px-4 py-2.5 rounded-xl bg-[#FAF8F4] border border-[#E8E3DA] text-sm outline-none focus:ring-2 focus:ring-[#6F8F72]" />
        <p className="text-xs text-[#9A938B] mb-3">
          {mode === "email"
            ? "Each email gets lifetime free Pro — applied now if they have an account, or automatically when they sign up."
            : "Anyone signing up with this domain gets lifetime free Pro, for the window above. Existing matching users are granted now."}
        </p>
        <button data-testid="clinic-submit-btn" onClick={submit} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-extrabold text-white bg-[#6F8F72] hover:bg-[#61805f] disabled:opacity-60 transition-colors">
          <Plus size={16} /> {busy ? "Granting…" : "Grant lifetime Pro"}
        </button>
      </div>

      {/* Grouped list */}
      {!groups.length ? (
        <EmptyState label="clinic comps" />
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.clinic_name} className="bg-white rounded-2xl border border-[#E8E3DA] p-4" data-testid={`clinic-group-${g.clinic_name}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-extrabold text-[#2A2724] flex items-center gap-2"><Building2 size={16} color="#6F8F72" /> {g.clinic_name}</p>
                  <p className="text-xs text-[#9A938B] mt-0.5">{g.email_rules} email · {g.domain_rules} domain {g.active ? "" : "· inactive"}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-[#6F8F72]">{g.total_granted}</p>
                  <p className="text-xs text-[#9A938B]">staff redeemed</p>
                </div>
              </div>
              <div className="mt-3 divide-y divide-[#F0ECE4] border-t border-[#F0ECE4]">
                {g.rules.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 py-2 flex-wrap" data-testid={`clinic-rule-${r.id}`}>
                    <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${r.type === "domain" ? "bg-[#DCE7DD] text-[#456B49]" : "bg-[#F6DED5] text-[#B54828]"}`}>{r.type}</span>
                    <span className="font-semibold text-[#2A2724] break-all text-sm">{r.value}</span>
                    {r.type === "domain" && r.enrollment_expires_at ? (
                      <span className="text-xs text-[#9A938B]">closes {fmtDate(r.enrollment_expires_at)}</span>
                    ) : null}
                    <span className="text-xs text-[#9A938B]">· {r.granted_count} granted</span>
                    {!r.active ? <span className="text-xs font-bold text-[#8A837A]">inactive</span> : null}
                    {r.active ? (
                      <button data-testid={`clinic-deactivate-${r.id}`} onClick={() => deactivate(r.id)} className="ml-auto p-2 rounded-full text-[#B54828] hover:bg-[#F6DED5] transition-colors"><Trash2 size={15} /></button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OptInsTable({ rows }) {
  if (!rows.length) return <EmptyState label="SMS opt-ins" />;
  return (
    <div className="bg-white rounded-2xl border border-[#E8E3DA] overflow-hidden" data-testid="sms-optins-table">
      <div className="px-5 py-3 bg-[#FAF8F4] border-b border-[#F0ECE4]">
        <p className="text-xs text-[#6B6560] font-semibold">
          Audit log of explicit SMS consent captured via the public opt-in page. Records are
          retained for compliance and are not editable.
        </p>
      </div>
      <div className="divide-y divide-[#F0ECE4]">
        {rows.map((o, i) => (
          <div key={o.id || `${o.phone}-${i}`} className="px-5 py-4" data-testid={`sms-optin-row-${i}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <p className="font-bold text-[#2A2724] font-mono">{o.phone}</p>
              {o.name ? <span className="text-sm text-[#4A4746]">· {o.name}</span> : null}
              <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-[#D6EAD8] text-[#2F6B36]">
                Consent ✓
              </span>
              <span className="text-xs text-[#9A938B] ml-auto">{fmtDateTime(o.created_at)}</span>
            </div>
            {o.consent_text ? (
              <p className="text-xs text-[#6B6560] mt-2 bg-[#FAF8F4] rounded-xl px-3 py-2 border border-[#F0ECE4] leading-relaxed">
                “{o.consent_text}”
              </p>
            ) : null}
            <p className="text-[11px] text-[#B7B0A6] mt-1.5">
              {o.source ? `Source: ${o.source}` : ""}
              {o.ip ? ` · IP: ${o.ip}` : ""}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
